import { Tooltip, Tag, Button, Typography, Avatar, Dropdown, Flex } from "antd";
import {
  EyeOutlined,
  BankOutlined,
  MoreOutlined,
  SyncOutlined,
  CloseOutlined,
  CheckOutlined,
  CopyOutlined,
  FileTextOutlined,
  EyeTwoTone,
  UserSwitchOutlined
} from "@ant-design/icons";
import moment from "moment";
import { CopyTextToClipboard } from "../../helpers/copy";
import {
  calculateTimeDifference,
  FormatTimeAgo,
} from "../../helpers/formatTimeAgo";
import {
  FormatDataCustomerJson,
  FormatDataSettleSlip,
} from "../../helpers/formatDataJson";
import { tagsBageStatus, tagsColorAlert } from "../../helpers/tagsBage";
import { BankData } from "../../Data/BankData";
import { formatNote, formatPromptPayNumber, hasPermission } from "../../utils";
import { getIcon } from "../../helpers/getIcon";
import { getTableRowClassName } from "../../helpers/getRowClassName";

const { Text } = Typography;

export const ColumnsTab1 = (
  t,
  {
    handleShow,
    Manual_QR,
    funRejected,
    setSwitchLoadding,
    setSwitchLoadding2,
    dropdownPlacement,
    activeBank,
    handleReSend,
    spinIcon,
    handlCheckStatusTransection,
    CheckStatusManual_Tranfer,
    currentLanguage,
    handlePaymentVoucher,
    detailsCustomer,
    resProfileAdmin,
    handleRefund
  }
) => [
    // ID
    {
      title: t("ID"),
      key: "id",
      dataIndex: "id",
      render: (id, record) => {
        const userPermissions = Array.isArray(resProfileAdmin?.permissions) ? resProfileAdmin.permissions : [];
        const accessible = hasPermission(userPermissions, "details_payout");

        return (
          <div
            onClick={() => accessible ? handleShow(record) : alert(t("You do not have permission to view details."))}
            className="text-truncate"
            title={id}
            style={{
              textWrap: "nowrap",
              width: 60,
              display: "inline-block",
              cursor: accessible ? "pointer" : "not-allowed",
              color: "#3eacff",
            }}
          >
            <u>#{id}</u>
          </div>
        );
      },
      sorter: false,
    },

    // Ref
    {
      title: t("Ref"),
      dataIndex: "logUuid",
      key: "logUuid",
      render: (ref) => (
        <Flex align="center" gap={5}>
          <Tooltip placement="topLeft" color="#3eacff" title={ref}>
            <span className="text-truncate" style={{ textWrap: "nowrap", maxWidth: "120px" }}>
              {ref}
            </span>
          </Tooltip>
          <CopyOutlined
            style={{ cursor: "pointer" }}
            className="hover"
            onClick={() => CopyTextToClipboard(ref)}
          />
        </Flex>
      ),
    },

    // CREATED DATE & SUCCESS TIME
    {
      title: (
        <>
          {t("CREATED DATE")}<br /> {t("SUCCESS TIME")}
        </>
      ),
      dataIndex: "created_at",
      key: "created_at",
      render: (date, record) => {
        const dFormat = currentLanguage === "lao" ? "lo" : currentLanguage;
        const created_at = moment(date).locale(dFormat).format("ll");
        const createdTime = moment(date).format("HH:mm:ss");
        const statusSuccess = record?.status === "SUCCESS";

        const settleSlip = record?.settleSlip ? FormatDataSettleSlip(record.settleSlip) : null;
        const updatedDateTime = settleSlip?.updatedDateTime || record?.updated_at;

        const updatedDate = updatedDateTime ? moment(updatedDateTime).locale(dFormat).format("ll") : null;
        const updatedTime = updatedDateTime ? moment(updatedDateTime).format("HH:mm:ss") : null;

        return (
          <span style={{ maxWidth: "60px" }} className="text-truncate">
            {created_at} <span style={{ fontWeight: "700", fontSize: 12 }}>{createdTime}</span>
            <br />
            {statusSuccess ? (
              <>{updatedDate} <span style={{ fontWeight: "500" }}>{updatedTime}</span></>
            ) : "-"}
          </span>
        );
      },
    },

    // customer bank & customer name
    {
      title: (
        <span className="text-truncate">
          {t("customer bank")} <br /> {t("customer name")}
        </span>
      ),
      render: (record) => {
        const to = FormatDataCustomerJson(record?.bank);
        const bankCode = ["kkp", "TTB"].includes(to?.bankCode) ? to.bankCode.toUpperCase() : to?.bankCode;
        const bankLogo = BankData?.find(o => o?.bank_id === bankCode || o?.bankCode === bankCode)?.bankLogo;

        const userPermissions = Array.isArray(resProfileAdmin?.permissions)
          ? resProfileAdmin.permissions
          : [];
        const accessible = hasPermission(userPermissions, "details_customer");

        return (
          <span style={{ textTransform: "uppercase" }}>
            <Avatar size={24} src={bankLogo} />
            {" - "}
            <b>
              {formatPromptPayNumber(to?.accountNo)}{" "}
              <CopyOutlined style={{ cursor: "pointer" }} className="hover" onClick={() => CopyTextToClipboard(to?.accountNo)} />
            </b>
            <br />
            <span style={{ fontWeight: "600" }}>
              <Tooltip color="blue" title={t("Customer details")}>
                <span className="hover" style={{ cursor: "pointer" }} onClick={() => {
                  if (accessible) {
                    detailsCustomer(record);
                  } else {
                    alert(t("You don't have permission to view customer details"));
                  }
                }}>
                  <EyeTwoTone /> {to?.accountName}
                </span>
              </Tooltip>{" "}
              <CopyOutlined style={{ cursor: "pointer" }} className="hover" onClick={() => CopyTextToClipboard(to?.accountName)} />
            </span>
          </span>
        );
      },
    },

    // status
    {
      title: t("status"),
      dataIndex: "status",
      key: "status",
      render: (status, record) => {
        const isFailed = status === "VERRIFY_FAILED";
        const isLoading = record?.id === setSwitchLoadding;
        return (
          <Flex align="center" gap={4}>
            <Tag
              style={{ textTransform: "capitalize", fontWeight: "500" }}
              color={tagsBageStatus(status)}
              icon={getIcon(status)}
              className={getTableRowClassName(status)}
            >
              {t(status)}
            </Tag>
            {isFailed && (
              isLoading ?
                <i className="bx bx-loader bx-spin" style={{ color: "rgba(0,0,0,0.41)" }} /> :
                <i title="Update Status" style={{ cursor: "pointer", fontSize: "19px" }} onClick={() => handlCheckStatusTransection(record)} className="bx bxs-edit hover" />
            )}
          </Flex>
        );
      },
    },

    // withdraw amt & transfer amt
    {
      title: (
        <span className="text-truncate">
          {t("withdraw amt")} <br /> {t("tarnsfer amt")}
        </span>
      ),
      dataIndex: "amount",
      render: (amount, record) => (
        <div style={{ textTransform: "uppercase" }}>
          <b className="text-truncate">THB {Intl.NumberFormat().format(parseFloat(amount).toFixed(2))}</b>
          <br />
          <span className="text-truncate">
            THB {Intl.NumberFormat().format(parseFloat(record?.transferAmount || amount).toFixed(2))}
          </span>
        </div>
      ),
    },

    // TRANSACTION DATE & COMPLETION TIME
    {
      title: (
        <>
          {t("TRANSACTION DATE")} <br /> {t("COMPLETION TIME")}
        </>
      ),
      render: (record) => {
        const isSuccess = record?.status === "SUCCESS";
        const created_at = record?.created_at;
        const updated_at = record?.transactionDateTime || record?.updated_at;

        if (!isSuccess) return "-";
        return (
          <>
            {updated_at ? FormatTimeAgo(updated_at) : "-"} <br />
            {created_at && updated_at ? calculateTimeDifference(created_at, updated_at) : "-"}
          </>
        );
      },
    },

    // fee
    {
      title: <span className="text-truncate">{t("fee")}</span>,
      dataIndex: "fee",
      render: (fee, record) => {
        const isSuccess = record?.status === "SUCCESS";
        const formattedFee = Intl.NumberFormat().format(parseFloat(fee).toFixed(2));
        return isSuccess ? <b>{formattedFee}</b> : <Text delete type="danger">{formattedFee}</Text>;
      },
    },

    // remark
    {
      title: <span className="text-truncate">{t("remark")}</span>,
      dataIndex: "note",
      width: 150,
      render: (note, record) => {
        const form = FormatDataSettleSlip(record?.settleSlip);
        const status = record?.status;
        const noteTxt = formatNote(note, status);
        const isError = form?.status === false;

        if (status === "SUCCESS") {
          const msg = form?.message ? t(form.message) : (record?.transactionDateTime === null ? <>{t(noteTxt)} <b>({t("WithdrawalManual")})</b></> : t(noteTxt));
          return <div style={{ fontSize: "11px", width: 150 }} className="text-wrap">{msg}</div>;
        }

        return (
          <div style={{ fontSize: "11px", width: 150, color: tagsColorAlert(status) }} className="text-wrap">
            <em>{isError ? (form?.error || t(noteTxt)) : t(noteTxt)}</em>
          </div>
        );
      },
    },

    // action
    {
      title: <span className="text-truncate">{t("action")}</span>,
      render: (_, record) => {
        const status = record.status;

        const arrayData = Array.isArray(activeBank) ? activeBank : [];
        const hasActiveKbiz = arrayData.some(o => ["k-biz", "k-biz-qr"].includes(o.channel) && o.status_bank === "Active" && o.accountType === "withdrawal");

        // สถานะที่อนุญาตให้ดำเนินการต่อได้
        // const isPending = ["PENDING", "PROCESSING", "INQ_MANUAL"].includes(status);

        // แสดงปุ่ม Manual (ถอนมือ) เฉพาะเมื่อสถานะเป็น INQ_MANUAL และมีบัญชี K-Biz ที่ใช้งานอยู่
        const isManual = ["INQ_MANUAL"].includes(status) && hasActiveKbiz;

        // ซ่อนปุ่มโอนสำเร็จและปุ่มปฏิเสธเมื่อสถานะเป็น "SUCCESS", "REJECTED", "REQUESTING"
        const hiddenAction = ["SUCCESS", "REJECTED"].includes(status);

        // แสงปุ่ม Payment Voucher เฉพาะเมื่อสถานะเป็น SUCCESS และมี settleSlip
        const hasSettleSlip = ["SUCCESS"].includes(status) && record?.settleSlip;

        // แสงปุ่ม Refund เฉพาะเมื่อสถานะเป็น SUCCESS 
        const hasRefund = ["SUCCESS"].includes(status);


        const userPermissions = Array.isArray(resProfileAdmin?.permissions) ? resProfileAdmin.permissions : [];
        const menuItems = [];

        // 1. Details
        if (hasPermission(userPermissions, "details_payout")) {
          menuItems.push({
            key: "Details",
            icon: <EyeOutlined />,
            label: t("Details"),
            onClick: () => handleShow(record),
          });
        }

        // 2. Payment Voucher
        if (hasPermission(userPermissions, "paymentVoucher_payout") && hasSettleSlip) {
          menuItems.push({
            key: "paymentVoucher",
            icon: <FileTextOutlined />,
            label: t("Payment Voucher"),
            onClick: () => handlePaymentVoucher(record),
          });
        }
        // 2.1 Refund
        if (hasPermission(userPermissions, "reject_payout") && hasRefund) {
          menuItems.push({
            key: "Refund",
            icon: <UserSwitchOutlined spin={record.id === spinIcon} />,
            label: <Tooltip placement="left" title={t(`Tooltip_refund`)}>
            {t("ไม่พบรายการโอน")}</Tooltip>,
            onClick: () => funRejected(false, record, "REFUND"),
          });
        }
        // 3. Re-Send
        if (hasPermission(userPermissions, "callback_payout")) {
          menuItems.push({
            key: "Re-Send",
            icon: <SyncOutlined spin={record.id === spinIcon} />,
            label: t("Re-Send"),
            onClick: () => handleReSend(record),
          });
        }

        // 4. Manual Withdraw (ถอนมือ)
        if (hasPermission(userPermissions, "Manual_payout") && isManual) {
          menuItems.push({
            key: "ถอนมือ",
            icon: <BankOutlined />,
            label: t("Manual"),
            onClick: () => Manual_QR(record),
            disabled: setSwitchLoadding === record.id,
          });
        }

        // 5. Transfer Success (โอนสำเร็จ)
        if (hasPermission(userPermissions, "approve_payout") && !hiddenAction) {
          menuItems.push({
            key: "โอนสำเร็จ",
            icon: <CheckOutlined />,
            label: t("Tranfer success"),
            onClick: () => CheckStatusManual_Tranfer(record.id),
            disabled: setSwitchLoadding2 === record.id,
            style: { color: "green" },
          });
        }

        // 6. Rejected
        if (hasPermission(userPermissions, "reject_payout") && !hiddenAction) {
          const isRequesting = ["REQUESTING"].includes(status);
          const rejectLabel = t("rejected");
          menuItems.push({
            key: "Rejected",
            icon: <CloseOutlined />,
            label: isRequesting ? (
              <Tooltip title={t("tooltip_withdraw_rejected")}>
                <span>{rejectLabel}</span>
              </Tooltip>
            ) : (rejectLabel),
            onClick: () => funRejected(false, record, "REJECTED"),
            style: { color: "red" },
          });
        }

        return (
          <div className="md-show">
            {menuItems.length > 0 && (<Dropdown
              arrow
              trigger={["click"]}
              placement={dropdownPlacement}
              menu={{ items: menuItems }}
            >
              <Button shape="circle">
                <MoreOutlined style={{ color: "#5A5A5A" }} />
              </Button>
            </Dropdown>)}

          </div>
        );
      },
    },
  ];